import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Messages } from 'primereact/messages';
import { MultiSelect } from 'primereact/multiselect';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import pinSettingsApi from '../api/pinSettingsApi';

const PIN_LENGTH = 4;
const DEFAULT_PIN_SETTINGS = { enabled: false, durationMinutes: null, configured: false, targetAccessPointIds: [] };

const getApiErrorMessage = (error, fallbackMessage) => {
  const data = error && error.response && error.response.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') return data.detail || data.message || data.title || fallbackMessage;
  return (error && error.message) || fallbackMessage;
};

const AccessPointPinSettingsDialog = ({ visible, accessPoint, onHide }) => {
  const msgs = useRef(null);
  const toast = useRef(null);
  const accessPointId = accessPoint && accessPoint.id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_PIN_SETTINGS);
  const [targetAccessPoints, setTargetAccessPoints] = useState([]);

  const showError = (error, fallbackMessage) => {
    const detail = getApiErrorMessage(error, fallbackMessage);
    if (msgs.current) msgs.current.show({ severity: 'error', summary: '', detail, sticky: true });
    if (toast.current) toast.current.show({ severity: 'error', summary: 'Error', detail, life: 5000 });
  };

  useEffect(() => {
    if (!visible || !accessPointId) return;

    const load = async () => {
      try {
        setLoading(true);
        setSettings(DEFAULT_PIN_SETTINGS);
        setTargetAccessPoints([]);
        if (msgs.current) msgs.current.clear();
        const locationId = Number(accessPoint.locationId || (accessPoint.location && accessPoint.location.id));
        const customerId = Number(accessPoint.customerId || (accessPoint.location && accessPoint.location.customerId) || (accessPoint.client && accessPoint.client.customerId));
        const targetsRequest = locationId > 0 || customerId > 0
          ? pinSettingsApi.getTargets(locationId > 0 ? locationId : null, customerId > 0 ? customerId : null).catch((error) => {
            showError(error, 'Failed to load target tiles.');
            return [];
          })
          : Promise.resolve([]);
        const results = await Promise.all([pinSettingsApi.get(accessPointId), targetsRequest]);
        const response = results[0];
        setTargetAccessPoints(Array.isArray(results[1]) ? results[1] : []);
        setSettings(response && response.configured ? {
          enabled: Boolean(response.enabled),
          durationMinutes: Number(response.durationMinutes) || null,
          configured: true,
          targetAccessPointIds: Array.isArray(response.targetAccessPointIds) ? response.targetAccessPointIds.map(Number).filter((id) => id > 0) : [],
        } : DEFAULT_PIN_SETTINGS);
      } catch (error) {
        showError(error, 'Failed to load PIN settings.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [visible, accessPointId]);

  const save = async () => {
    const durationMinutes = Number(settings.durationMinutes);
    if (settings.enabled && (!Number.isFinite(durationMinutes) || durationMinutes <= 0)) {
      showError(null, 'PIN duration minutes is required and must be greater than 0.');
      return;
    }

    try {
      setSaving(true);
      if (msgs.current) msgs.current.clear();
      await pinSettingsApi.save(accessPointId, {
        durationMinutes: Number.isFinite(durationMinutes) ? durationMinutes : 0,
        pinLength: PIN_LENGTH,
        enabled: Boolean(settings.enabled),
        targetAccessPointIds: Array.isArray(settings.targetAccessPointIds) ? settings.targetAccessPointIds.map(Number).filter((id) => id > 0 && id !== Number(accessPointId)) : [],
      });
      setSettings({ ...settings, durationMinutes: Number.isFinite(durationMinutes) ? durationMinutes : null, configured: true });
      if (toast.current) toast.current.show({ severity: 'success', summary: 'Successful', detail: 'PIN settings saved successfully.', life: 3000 });
    } catch (error) {
      showError(error, 'Failed to save PIN settings.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      setSaving(true);
      if (msgs.current) msgs.current.clear();
      await pinSettingsApi.remove(accessPointId);
      setSettings(DEFAULT_PIN_SETTINGS);
      if (toast.current) toast.current.show({ severity: 'success', summary: 'Successful', detail: 'PIN settings removed successfully.', life: 3000 });
    } catch (error) {
      showError(error, 'Failed to remove PIN settings.');
    } finally {
      setSaving(false);
    }
  };

  return <Dialog header="Access PIN Settings" visible={visible} onHide={saving ? undefined : onHide} style={{ width: '32rem', maxWidth: '95vw' }}>
    <Toast ref={toast} />
    <Messages ref={msgs} />
    <p>Configure temporary Ajax keypad PIN generation after a successful scan for this access point.</p>
    {loading ? <ProgressBar mode="indeterminate" style={{ height: '6px' }} /> : <div className="p-fluid">
      <div className="mb-3">
        <label htmlFor="pinEnabled">
          <input id="pinEnabled" type="checkbox" checked={Boolean(settings.enabled)} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} disabled={saving} className="mr-2" />
          Enable PIN generation
        </label>
      </div>
      <div className="mb-3">
        <label htmlFor="pinDurationMinutes" className="d-block mb-2">PIN duration minutes</label>
        <InputNumber id="pinDurationMinutes" value={settings.durationMinutes} min={0} minFractionDigits={0} maxFractionDigits={0} useGrouping={false} onValueChange={(e) => setSettings({ ...settings, durationMinutes: e.value })} disabled={saving} />
      </div>
      <div className="mb-3">
        <label htmlFor="pinTargetAccessPoints" className="d-block mb-2">Target tiles</label>
        <MultiSelect id="pinTargetAccessPoints" value={settings.targetAccessPointIds} options={targetAccessPoints.filter((target) => Number(target.id) !== Number(accessPointId)).map((target) => ({ label: `${target.tile || ''} - ${target.gate || ''}`, value: Number(target.id) }))} onChange={(e) => setSettings({ ...settings, targetAccessPointIds: e.value })} display="chip" filter placeholder="Select target tiles" disabled={saving} style={{ width: '100%' }} />
        <small>Optional. If this access point has no Ajax hub/relay configuration, the generated PIN will be sent to the selected target tiles instead.</small>
      </div>
      <div className="flex flex-wrap gap-3">
        <Button label="Save PIN Settings" icon="pi pi-save" onClick={save} loading={saving} disabled={saving} />
        <Button label="Remove PIN Settings" icon="pi pi-trash" className="p-button-danger" onClick={remove} loading={saving} disabled={saving || !settings.configured} />
      </div>
    </div>}
  </Dialog>;
};

export default AccessPointPinSettingsDialog;